import React from "react";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { formatDueDate } from "./due-date";
import { Suggestion } from "./suggestions";
import { IssueCard as IssueCardData } from "./types";

/**
 * The Suggested lane — a triage column that sits before the real statuses.
 *
 * It is NOT a status: nothing lives here, nothing is dragged in or out, and a card in the lane is
 * still sitting in its own column. That difference has to be visible, which is why the lane keeps
 * a tinted ground and a dashed edge instead of borrowing the column chrome — a lane that looked
 * like a column would read as a workflow step nobody configured.
 *
 * Each card states its reason in plain words and offers the two moves that actually clear it:
 * take it, or say it is not a problem.
 */
export interface SuggestedLaneProps {
  suggestions: Suggestion[];
  onOpen: (card: IssueCardData) => void;
  onAssignToMe: (card: IssueCardData) => void;
  onDismiss: (card: IssueCardData) => void;
  onHideLane: () => void;
}

const SuggestedLane: React.FC<SuggestedLaneProps> = ({ suggestions, onOpen, onAssignToMe, onDismiss, onHideLane }) => {
  // No suggestions is the good state, and an empty lane permanently occupying a column's width
  // to say so would be the worst trade on the board.
  if (suggestions.length === 0) return null;

  return (
    <section className="kuika-board__suggested" aria-label={`Suggested, ${suggestions.length} issues`}>
      <div className="kuika-board__suggested-header">
        {/* A bulb, not a sparkle: these are deterministic rules, and an AI mark would promise
            something the lane does not do. */}
        <KuikaSvg name="bulb" width={13} height={13} />
        <span className="kuika-board__suggested-title">Suggested</span>
        <span className="kuika-board__suggested-count">{suggestions.length}</span>
        <button
          type="button"
          className="kuika-board__suggested-hide"
          onClick={onHideLane}
          title="Hide suggestions"
          aria-label="Hide suggestions"
        >
          <KuikaSvg name="x" width={12} height={12} />
        </button>
      </div>
      <div className="kuika-board__suggested-list">
        {suggestions.map((suggestion: Suggestion) => {
          const due: string | null = formatDueDate(suggestion.card.dueDate);
          return (
            <div
              key={suggestion.card.id}
              className="kuika-board__suggested-card"
              data-testid={`board-suggestion-${suggestion.card.key}`}
            >
              <div
                className="kuika-board__suggested-card-body"
                role="button"
                tabIndex={0}
                onClick={() => onOpen(suggestion.card)}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  onOpen(suggestion.card);
                }}
              >
                <div className="kuika-board__suggested-card-summary">{suggestion.card.summary}</div>
                <div className="kuika-board__suggested-card-meta">
                  <span className="kuika-board__card-key">{suggestion.card.key}</span>
                  {due && <span className="kuika-board__suggested-card-due">{due}</span>}
                </div>
                <div className={`kuika-board__suggested-reason kuika-board__suggested-reason--${suggestion.rule}`}>
                  {suggestion.reason}
                </div>
              </div>
              <div className="kuika-board__suggested-actions">
                {/*
                  The primary action has to be the one that CLEARS the reason. "Assign to me" on
                  an overdue card is a trap: the click succeeds, the card stays in the lane, and
                  the lane looks broken. Only the unassigned rule is fixed by taking the card;
                  the rest are fixed in the issue itself.
                */}
                {suggestion.rule === "unassigned_important" ? (
                  <button
                    type="button"
                    className="kuika-board__suggested-action"
                    onClick={() => onAssignToMe(suggestion.card)}
                    data-testid={`board-suggestion-assign-${suggestion.card.key}`}
                  >
                    Assign to me
                  </button>
                ) : (
                  <button
                    type="button"
                    className="kuika-board__suggested-action"
                    onClick={() => onOpen(suggestion.card)}
                    data-testid={`board-suggestion-open-${suggestion.card.key}`}
                  >
                    {suggestion.rule === "overdue" ? "Reschedule" : "Open"}
                  </button>
                )}
                <button
                  type="button"
                  className="kuika-board__suggested-action kuika-board__suggested-action--quiet"
                  onClick={() => onDismiss(suggestion.card)}
                  data-testid={`board-suggestion-dismiss-${suggestion.card.key}`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SuggestedLane;
